import type { Meta, StoryObj } from "@storybook/react-vite"

import StaffLocationBillsIndexPage from "./location/bills/index"
import StaffLocationDashboardsShowPage from "./location/dashboards/show"
import StaffLocationManageBillingSettingsPage from "./location/manage/billing/settings"
import StaffLocationManageBusinessHoursShowPage from "./location/manage/business_hours/show"
import StaffLocationManageDashboardShowPage from "./location/manage/dashboard/show"
import StaffLocationManageEventsIndexPage from "./location/manage/events/index"
import StaffLocationManageLocationShowPage from "./location/manage/location/show"
import StaffLocationManageMenuIndexPage from "./location/manage/menu/index"
import StaffLocationManageSuppliesIndexPage from "./location/manage/supplies/index"
import StaffLocationManageTablesEditPage from "./location/manage/tables/edit"
import StaffLocationManageTablesIndexPage from "./location/manage/tables/index"
import StaffLocationManageTablesNewPage from "./location/manage/tables/new"
import StaffLocationManageTablesShowPage from "./location/manage/tables/show"
import StaffLocationManageTaxProfileShowPage from "./location/manage/tax-profile/show"
import StaffLocationManageUsersEditPage from "./location/manage/users/edit"
import StaffLocationManageUsersIndexPage from "./location/manage/users/index"
import StaffLocationManageUsersNewPage from "./location/manage/users/new"
import StaffLocationMenuShowPage from "./location/menu/show"
import StaffLocationOrdersIndexPage from "./location/orders/index"
import StaffLocationOrdersNewPage from "./location/orders/new"
import StaffLocationTablesIndexPage from "./location/tables/index"
import StaffLocationsIndexPage from "./locations/index"
import StaffLocationsNewPage from "./locations/new"
import StaffProfilesEditPasswordPage from "./profiles/edit-password"
import StaffProfilesEditPage from "./profiles/edit"
import StaffProfilesShowPage from "./profiles/show"
import StaffRegistrationsNewPage from "./registrations/new"
import StaffSessionsNewPage from "./sessions/new"
import StaffInviteShowPage from "./staff_invites/show"
import {
  createPageStory,
  sampleBillingLocation,
  sampleKanbanOrders,
  sampleBusinessHours,
  sampleLocationOnboarding,
  sampleTimezones,
  sampleManageDashboardProps,
  sampleManageTable,
  sampleManageTables,
  sampleManageUsers,
  sampleExtraGroups,
  sampleMenuCategories,
  sampleOperationalTableSummaries,
  sampleOrphanMenuItems,
  samplePagination,
  samplePaymentBills,
  sampleRoleOptions,
  sampleStaffSupplies,
  sampleStaffSupplyLedger,
  sampleStaffSupplyUnits,
  sampleServiceTypes,
  sampleStaffBills,
  sampleStaffEventRows,
  sampleStaffInvite,
  sampleStaffLocations,
  sampleStatusColumns,
  sampleTableOptions,
  sampleTableStatuses,
  staffPageProps,
} from "../storybook-helpers"

const sampleItemStatuses = [
  { value: "active", label: "Activo" },
  { value: "sold_out", label: "Agotado" },
  { value: "inactive", label: "Inactivo" },
]

const sampleItemStations = [
  { value: "kitchen", label: "Cocina" },
  { value: "bar", label: "Barra" },
  { value: "cashier", label: "Caja" },
  { value: "dessert", label: "Postres" },
]

const meta = {
  title: "Pages/Staff",
  tags: ["autodocs"],
  parameters: {
    layout: "fullscreen",
  },
} satisfies Meta

export default meta

type Story = StoryObj<typeof meta>

const sampleLocationDashboard = {
  tables: {
    total: 12,
    available: 6,
    occupied: 4,
    reserved: 1,
    disabled: 1,
  },
  active_orders: {
    pending: 2,
    in_progress: 3,
    ready: 1,
    total: 6,
  },
  revenue: {
    today_total: 1840,
    today_count: 9,
    pending_total: 620,
    pending_count: 3,
    currency: "MXN",
  },
  recent_activity: [
    {
      id: 7001,
      name: "Tacos al pastor",
      quantity: 2,
      status: "in_progress",
      service_type: "eat_in",
      line_total: 136,
      currency: "MXN",
      table_name: "Mesa 4",
      created_at: "2026-06-26T16:25:00.000Z",
    },
  ],
  table_updates: [
    {
      id: 9401,
      name: "Mesa 4",
      action: "update_record",
      change: { field: "status", from: "available", to: "occupied" },
      created_at: "2026-06-26T16:20:00.000Z",
    },
  ],
  order_updates: [
    {
      id: 9402,
      name: "Tacos al pastor",
      action: "update_record",
      change: { field: "status", from: "pending", to: "in_progress" },
      created_at: "2026-06-26T16:15:00.000Z",
    },
  ],
}

export const LocationBillsIndex: Story = createPageStory(StaffLocationBillsIndexPage, {
  name: "location/bills/index",
  url: "/bills",
  pageProps: {
    ...staffPageProps,
    bills: sampleStaffBills,
    pagination: samplePagination,
    search: "",
    // `null` is the shape a restaurant without `cfdi_invoicing` receives, and
    // the drawer then renders no invoice section at all. The workshop shows the
    // ordinary case rather than the Mexico-only one.
    cfdi: null,
    timeZone: "America/Mexico_City",
  },
})

/**
 * The CFDI issuer identity, with nothing entered yet — the state a restaurant
 * lands in the moment the platform grants it the feature, and the one the
 * screen has to explain rather than present as a blank form.
 */
export const LocationManageTaxProfileShow: Story = createPageStory(StaffLocationManageTaxProfileShowPage, {
  name: "location/manage/tax_profile/show",
  url: "/manage/tax_profile",
  pageProps: {
    ...staffPageProps,
    taxProfile: {
      configured: false,
      pacConfigured: false,
      rfc: null,
      legalName: null,
      taxRegimeCode: null,
      postalCode: null,
      defaultSeries: null,
      defaultProductCode: null,
      defaultUnitCode: null,
      pacProvider: "simulated",
      pacEnvironment: "sandbox",
    },
    taxRegimeOptions: [
      { value: "601", label: "General de ley personas morales" },
      { value: "612", label: "Personas físicas con actividades empresariales y profesionales" },
      { value: "626", label: "Régimen simplificado de confianza (RESICO)" },
    ],
    pacEnvironmentOptions: [
      { value: "sandbox", label: "Pruebas" },
      { value: "production", label: "Producción" },
    ],
  },
})

export const LocationDashboardsShow: Story = createPageStory(StaffLocationDashboardsShowPage, {
  name: "location/dashboards/show",
  url: "/dashboard",
  pageProps: {
    ...staffPageProps,
    dashboard: sampleLocationDashboard,
  },
})

export const LocationManageBillingSettings: Story = createPageStory(
  StaffLocationManageBillingSettingsPage,
  {
    name: "location/manage/billing/settings",
    url: "/manage/billing/settings",
    pageProps: {
      ...staffPageProps,
      location: sampleBillingLocation,
    },
  }
)

export const LocationManageBusinessHoursShow: Story = createPageStory(
  StaffLocationManageBusinessHoursShowPage,
  {
    name: "location/manage/business_hours/show",
    url: "/manage/location/business_hours",
    pageProps: {
      ...staffPageProps,
      businessHours: sampleBusinessHours,
      timezones: sampleTimezones,
    },
  }
)

export const LocationManageDashboardShow: Story = createPageStory(
  StaffLocationManageDashboardShowPage,
  {
    name: "location/manage/dashboard/show",
    url: "/manage",
    pageProps: {
      ...staffPageProps,
      ...sampleManageDashboardProps,
    },
  }
)

export const LocationManageEventsIndex: Story = createPageStory(
  StaffLocationManageEventsIndexPage,
  {
    name: "location/manage/events/index",
    url: "/manage/events",
    componentProps: {
      events: sampleStaffEventRows,
      pagination: samplePagination,
    },
    pageProps: staffPageProps,
  }
)

export const LocationManageLocationShow: Story = createPageStory(
  StaffLocationManageLocationShowPage,
  {
    name: "location/manage/location/show",
    url: "/manage/location",
    pageProps: {
      ...staffPageProps,
      location: sampleLocationOnboarding,
      businessHours: sampleBusinessHours,
      flash: {
        notice: "Configuración operativa guardada.",
      },
    },
  }
)

export const LocationManageMenuIndex: Story = createPageStory(StaffLocationManageMenuIndexPage, {
  name: "location/manage/menu/index",
  url: "/manage/menu",
  componentProps: {
    categories: sampleMenuCategories,
    orphanItems: sampleOrphanMenuItems,
    extraGroups: sampleExtraGroups,
    statuses: sampleItemStatuses,
    stations: sampleItemStations,
  },
  pageProps: staffPageProps,
})

export const LocationManageTablesEdit: Story = createPageStory(StaffLocationManageTablesEditPage, {
  name: "location/manage/tables/edit",
  url: "/manage/tables/tbl-4/edit",
  pageProps: {
    ...staffPageProps,
    table: sampleManageTable,
  },
})

export const LocationManageTablesIndex: Story = createPageStory(StaffLocationManageTablesIndexPage, {
  name: "location/manage/tables/index",
  url: "/manage/tables",
  pageProps: {
    ...staffPageProps,
    tables: sampleManageTables,
  },
})

export const LocationManageTablesNew: Story = createPageStory(StaffLocationManageTablesNewPage, {
  name: "location/manage/tables/new",
  url: "/manage/tables/new",
  pageProps: {
    ...staffPageProps,
    statuses: sampleTableStatuses,
    table: {
      name: "Mesa terraza",
      seats: 4,
      status: "available",
    },
  },
})

export const LocationManageTablesShow: Story = createPageStory(StaffLocationManageTablesShowPage, {
  name: "location/manage/tables/show",
  url: "/manage/tables/tbl-4",
  pageProps: {
    ...staffPageProps,
    table: sampleManageTable,
  },
})

export const LocationManageUsersEdit: Story = createPageStory(StaffLocationManageUsersEditPage, {
  name: "location/manage/users/edit",
  url: "/manage/users/61/edit",
  pageProps: {
    ...staffPageProps,
    roleOptions: sampleRoleOptions,
    user: {
      id: 61,
      userId: 17,
      name: "Mariana López",
      emailAddress: "mariana@menny.app",
      role: "manager",
    },
  },
})

export const LocationManageSuppliesIndex: Story = createPageStory(
  StaffLocationManageSuppliesIndexPage,
  {
    name: "location/manage/supplies/index",
    url: "/manage/supplies",
    componentProps: {
      supplies: sampleStaffSupplies,
      units: sampleStaffSupplyUnits,
      shortage: { reportable: true, covered: 12, total: 18 },
      pendingPurchases: { count: 2 },
      // What the drawer's partial reload returns for the first supply. There is
      // no server here, so this is the only way the drawer renders anything.
      ledger: sampleStaffSupplyLedger,
      timeZone: "America/Mexico_City",
    },
    pageProps: staffPageProps,
  }
)

export const LocationManageUsersIndex: Story = createPageStory(StaffLocationManageUsersIndexPage, {
  name: "location/manage/users/index",
  url: "/manage/users",
  pageProps: {
    ...staffPageProps,
    users: sampleManageUsers,
  },
})

export const LocationManageUsersNew: Story = createPageStory(StaffLocationManageUsersNewPage, {
  name: "location/manage/users/new",
  url: "/manage/users/new",
  pageProps: {
    ...staffPageProps,
    roleOptions: sampleRoleOptions,
    user: {
      role: "waiter",
    },
  },
})

export const LocationMenuShow: Story = createPageStory(StaffLocationMenuShowPage, {
  name: "location/menu/show",
  url: "/menu",
  componentProps: {
    categories: sampleMenuCategories,
    orphanItems: sampleOrphanMenuItems,
  },
  pageProps: staffPageProps,
})

export const LocationOrdersIndex: Story = createPageStory(StaffLocationOrdersIndexPage, {
  name: "location/orders/index",
  url: "/orders",
  pageProps: {
    ...staffPageProps,
    orders: sampleKanbanOrders,
    statusColumns: sampleStatusColumns,
  },
})

export const LocationOrdersNew: Story = createPageStory(StaffLocationOrdersNewPage, {
  name: "location/orders/new",
  url: "/orders/new",
  pageProps: {
    ...staffPageProps,
    categories: sampleMenuCategories,
    orphanItems: sampleOrphanMenuItems,
    tables: sampleTableOptions,
    serviceTypes: sampleServiceTypes,
    activeOrders: sampleKanbanOrders,
    activeBills: samplePaymentBills,
    defaultValues: {
      tableId: "tbl-4",
      billId: "5001",
      serviceType: "eat_in",
    },
  },
})

export const LocationTablesIndex: Story = createPageStory(StaffLocationTablesIndexPage, {
  name: "location/tables/index",
  url: "/tables",
  pageProps: {
    ...staffPageProps,
    tables: sampleOperationalTableSummaries,
    orderForm: {
      categories: sampleMenuCategories,
      orphanItems: sampleOrphanMenuItems,
      ordersErrors: [],
    },
    selectedTableExternalId: null,
  },
})

export const LocationsIndex: Story = createPageStory(StaffLocationsIndexPage, {
  name: "locations/index",
  url: "/locations",
  pageProps: {
    ...staffPageProps,
    locations: sampleStaffLocations,
  },
})

export const LocationsNew: Story = createPageStory(StaffLocationsNewPage, {
  name: "locations/new",
  url: "/locations/new",
  pageProps: staffPageProps,
})

export const ProfilesEditPassword: Story = createPageStory(StaffProfilesEditPasswordPage, {
  name: "profiles/edit-password",
  url: "/profile/password",
  pageProps: staffPageProps,
})

export const ProfilesEdit: Story = createPageStory(StaffProfilesEditPage, {
  name: "profiles/edit",
  url: "/profile/edit",
  pageProps: staffPageProps,
})

export const ProfilesShow: Story = createPageStory(StaffProfilesShowPage, {
  name: "profiles/show",
  url: "/profile",
  pageProps: staffPageProps,
})

export const RegistrationsNew: Story = createPageStory(StaffRegistrationsNewPage, {
  name: "registrations/new",
  url: "/registration/new",
  pageProps: {},
})

export const SessionsNew: Story = createPageStory(StaffSessionsNewPage, {
  name: "sessions/new",
  url: "/session/new",
  pageProps: {
    flash: {
      alert: "Necesitás iniciar sesión para continuar.",
    },
  },
})

export const StaffInvitesShow: Story = createPageStory(StaffInviteShowPage, {
  name: "staff_invites/show",
  url: "/staff_invite",
  componentProps: {
    invite: sampleStaffInvite,
  },
  pageProps: staffPageProps,
})
